import { useMemo } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import gql from 'graphql-tag'
import isNil from 'lodash.isnil'
import {
  CreateViewDashboardMutation,
  CreateViewDashboardMutationVariables,
  CreateViewDashboardViewCsvMutation,
  CreateViewDashboardViewCsvMutationVariables,
  DeleteViewDashboardMutation,
  DeleteViewDashboardMutationVariables,
  GetPublishedDashboardsByOrganizationQuery,
  GetPublishedDashboardsByOrganizationQueryVariables,
  GetViewDashboardByIdQuery,
  GetViewDashboardByIdQueryVariables,
  GetViewDashboardByUuidQuery,
  GetViewDashboardByUuidQueryVariables,
  GetViewDashboardsQuery,
  GetViewDashboardsQueryVariables,
  GetViewDashboardViewDataInput,
  GetViewsDataCountsForViewDashboardQuery,
  GetViewsDataCountsForViewDashboardQueryVariables,
  GetViewsDataForViewDashboardQuery,
  GetViewsDataForViewDashboardQueryVariables,
  UpdateViewDashboardMutation,
  UpdateViewDashboardMutationVariables,
} from 'types/graphql'

import { QueryKey, useMutation, useQuery, UseQueryOptions } from 'src/utils/queryClient'

const GetByRecordType = gql`
  query GetViewDashboardsQuery($recordTypeId: Int!) {
    recordTypeById(id: $recordTypeId) {
      activeViewDashboards {
        ...ViewDashboardOverviewFragment
      }
    }
  }
`
export const useViewDashboardsForRecordTypeQuery = (recordTypeId: number) => {
  const { data, ...rest } = useQuery<GetViewDashboardsQuery, GetViewDashboardsQueryVariables>({
    queryKey: [QueryKey.RecordTypeViewDashboards, recordTypeId],
    queryDocument: GetByRecordType,
    variables: { recordTypeId },
  })
  const viewDashboards = useMemo(() => data?.recordTypeById?.activeViewDashboards || [], [data])
  return {
    viewDashboards,
    ...rest,
  }
}

const GetPublishedDashboardsByOrganization = gql`
  query GetPublishedDashboardsByOrganizationQuery($slug: String!) {
    organizationBySlug(slug: $slug) {
      id
      canSearchEntities
      gisSetup
      isPublicGISSearchEnabled
      entityLayers {
        ...EntityLayerFragment
      }
      publishedViewDashboards {
        ...ViewDashboardFragment
      }
    }
  }
`
export const usePublishedDashboardsByOrganizationQuery = (slug: string) => {
  const { data, ...rest } = useQuery<
    GetPublishedDashboardsByOrganizationQuery,
    GetPublishedDashboardsByOrganizationQueryVariables
  >({
    queryKey: [QueryKey.OrganizationPublishedViewDashboards, slug],
    queryDocument: GetPublishedDashboardsByOrganization,
    variables: { slug },
  })
  const entityLayers = useMemo(() => data?.organizationBySlug?.entityLayers || [], [data])
  const publishedViewDashboards = useMemo(
    () => data?.organizationBySlug?.publishedViewDashboards || [],
    [data]
  )
  return {
    canSearchEntities: !!data?.organizationBySlug?.canSearchEntities,
    entityLayers,
    publishedViewDashboards,
    ...rest,
  }
}

const GetById = gql`
  query GetViewDashboardByIdQuery($id: Int!) {
    viewDashboardById(id: $id) {
      ...ViewDashboardFragment
    }
  }
`
export const useViewDashboardByIdQuery = (
  id: number | undefined,
  options?: UseQueryOptions<GetViewDashboardByIdQuery, GetViewDashboardByIdQueryVariables>
) => {
  const { data, ...rest } = useQuery<GetViewDashboardByIdQuery, GetViewDashboardByIdQueryVariables>(
    {
      queryKey: [QueryKey.ViewDashboardById, id],
      queryDocument: GetById,
      variables: { id: id ?? -1 },
      ...options,
      enabled: !!id && (isNil(options?.enabled) || options.enabled),
    }
  )
  const viewDashboard = useMemo(() => data?.viewDashboardById, [data])
  return {
    viewDashboard,
    ...rest,
  }
}

const GetByUuid = gql`
  query GetViewDashboardByUuidQuery($uuid: String!) {
    viewDashboardByUuid(uuid: $uuid) {
      ...ViewDashboardFragment
    }
  }
`
export const useViewDashboardByUuidQuery = (uuid: string) => {
  const { data, ...rest } = useQuery<
    GetViewDashboardByUuidQuery,
    GetViewDashboardByUuidQueryVariables
  >({
    queryKey: [QueryKey.ViewDashboardByUuid, uuid],
    queryDocument: GetByUuid,
    variables: { uuid },
  })
  const viewDashboard = useMemo(() => data?.viewDashboardByUuid, [data])
  return {
    viewDashboard,
    ...rest,
  }
}

const Create = gql`
  mutation CreateViewDashboardMutation($input: ViewDashboardInput!) {
    createViewDashboard(input: $input) {
      ...ViewDashboardOverviewFragment
    }
  }
`
export const useCreateViewDashboardMutation = () => {
  return useMutation<CreateViewDashboardMutation, CreateViewDashboardMutationVariables>({
    mutationDocument: Create,
  })
}

const Delete = gql`
  mutation DeleteViewDashboardMutation($id: Int!) {
    deleteViewDashboard(id: $id)
  }
`
export const useDeleteViewDashboardMutation = () => {
  return useMutation<DeleteViewDashboardMutation, DeleteViewDashboardMutationVariables>({
    mutationDocument: Delete,
  })
}

const Update = gql`
  mutation UpdateViewDashboardMutation($id: Int!, $input: ViewDashboardInput!) {
    updateViewDashboard(id: $id, input: $input) {
      ...ViewDashboardOverviewFragment
    }
  }
`
export const useUpdateViewDashboardMutation = () => {
  return useMutation<UpdateViewDashboardMutation, UpdateViewDashboardMutationVariables>({
    mutationDocument: Update,
  })
}

const GetViewsDataForViewDashboard = gql`
  query GetViewsDataForViewDashboardQuery(
    $dashboardId: Int!
    $inputs: [GetViewDashboardViewDataInput!]!
  ) {
    getViewsDataForViewDashboard(dashboardId: $dashboardId, inputs: $inputs) {
      ...ViewDataFragment
    }
  }
`
export const useViewsDataForViewDashboardQuery = (
  dashboardId: number,
  inputs: GetViewDashboardViewDataInput[],
  options?: UseQueryOptions<
    GetViewsDataForViewDashboardQuery,
    GetViewsDataForViewDashboardQueryVariables
  >
) => {
  return useQuery<GetViewsDataForViewDashboardQuery, GetViewsDataForViewDashboardQueryVariables>({
    queryKey: [QueryKey.ViewsDataForViewDashboard, dashboardId, inputs],
    queryDocument: GetViewsDataForViewDashboard,
    variables: { dashboardId, inputs },
    placeholderData: keepPreviousData,
    ...options,
  })
}

export const GetViewsDataCountsForViewDashboard = gql`
  query GetViewsDataCountsForViewDashboardQuery(
    $dashboardId: Int!
    $inputs: [GetViewDashboardViewDataInput!]!
  ) {
    getViewsDataForViewDashboard(dashboardId: $dashboardId, inputs: $inputs) {
      viewId
      totalCount
    }
  }
`
export const useViewsDataCountsForViewDashboardQuery = (
  dashboardId: number | null | undefined,
  viewIds: number[]
) => {
  return useQuery<
    GetViewsDataCountsForViewDashboardQuery,
    GetViewsDataCountsForViewDashboardQueryVariables
  >({
    queryKey: [QueryKey.ViewsDataCountsForViewDashboard, dashboardId, viewIds],
    queryDocument: GetViewsDataCountsForViewDashboard,
    variables: {
      dashboardId: dashboardId ?? -1,
      inputs: viewIds.map((id) => ({ viewId: id })),
    },
    enabled: !!dashboardId && !!viewIds.length,
  })
}

const CreateViewDashboardViewCsv = gql`
  mutation CreateViewDashboardViewCsvMutation($input: CreateViewDashboardViewCsvInput!) {
    createViewDashboardViewCsv(input: $input) {
      csvFileId
      csvUrl
    }
  }
`
export const useCreateViewDashboardViewCsvMutation = () => {
  return useMutation<
    CreateViewDashboardViewCsvMutation,
    CreateViewDashboardViewCsvMutationVariables
  >({
    mutationDocument: CreateViewDashboardViewCsv,
  })
}
